import { ThemeMode } from '@/shared/store/app-store/types';
import { type HexColor } from '@/shared/types/common';

export const CUSTOM_PALETTE_STORAGE_KEY = 'custom_palette_name';

export enum ColorPalette {
  blueLight1 = 'blueLight1',
  blueDark1 = 'blueDark1',
  blueLight2 = 'blueLight2',
  blueDark2 = 'blueDark2',
  greenLight = 'greenLight',
  greenDark = 'greenDark',
  pinkLight = 'pinkLight',
  pinkDark = 'pinkDark',
}

type ThemeVariableName = `--tg-theme-${string}`;

export const COLOR_PALETTES_CONFIG: Record<
  ColorPalette,
  { [key in ThemeVariableName]: HexColor } & { theme: ThemeMode }
> = {
  [ColorPalette.blueLight1]: {
    theme: ThemeMode.LIGHT,
    '--tg-theme-accent-text-color': '#168dcd',
    '--tg-theme-bg-color': '#ffffff',
    '--tg-theme-button-color': '#40a7e3',
    '--tg-theme-button-text-color': '#ffffff',
    '--tg-theme-destructive-text-color': '#d14e4e',
    '--tg-theme-header-bg-color': '#ffffff',
    '--tg-theme-hint-color': '#999999',
    '--tg-theme-link-color': '#168dcd',
    '--tg-theme-secondary-bg-color': '#f1f1f1',
    '--tg-theme-section-bg-color': '#ffffff',
    '--tg-theme-section-header-text-color': '#168dcd',
    '--tg-theme-subtitle-text-color': '#999999',
    '--tg-theme-text-color': '#000000',
  },
  [ColorPalette.blueDark1]: {
    theme: ThemeMode.DARK,
    '--tg-theme-accent-text-color': '#6ab3f2',
    '--tg-theme-bg-color': '#17212b',
    '--tg-theme-button-color': '#5289c1',
    '--tg-theme-button-text-color': '#ffffff',
    '--tg-theme-destructive-text-color': '#ec3942',
    '--tg-theme-header-bg-color': '#17212b',
    '--tg-theme-hint-color': '#708599',
    '--tg-theme-link-color': '#6ab3f3',
    '--tg-theme-secondary-bg-color': '#232e3c',
    '--tg-theme-section-bg-color': '#17212b',
    '--tg-theme-section-header-text-color': '#6ab3f3',
    '--tg-theme-subtitle-text-color': '#708599',
    '--tg-theme-text-color': '#f5f5f5',
  },
  [ColorPalette.blueLight2]: {
    theme: ThemeMode.LIGHT,
    '--tg-theme-accent-text-color': '#1aa6d0',
    '--tg-theme-bg-color': '#ffffff',
    '--tg-theme-button-color': '#45bee7',
    '--tg-theme-button-text-color': '#ffffff',
    '--tg-theme-destructive-text-color': '#d14e4e',
    '--tg-theme-header-bg-color': '#ffffff',
    '--tg-theme-hint-color': '#999999',
    '--tg-theme-link-color': '#1aa6d0',
    '--tg-theme-secondary-bg-color': '#f1f1f1',
    '--tg-theme-section-bg-color': '#ffffff',
    '--tg-theme-section-header-text-color': '#1aa6d0',
    '--tg-theme-subtitle-text-color': '#999999',
    '--tg-theme-text-color': '#000000',
  },
  [ColorPalette.blueDark2]: {
    theme: ThemeMode.DARK,
    '--tg-theme-accent-text-color': '#64d5fa',
    '--tg-theme-bg-color': '#1a2c33',
    '--tg-theme-button-color': '#58bfe8',
    '--tg-theme-button-text-color': '#ffffff',
    '--tg-theme-destructive-text-color': '#ec3942',
    '--tg-theme-header-bg-color': '#1a2c33',
    '--tg-theme-hint-color': '#83a8b7',
    '--tg-theme-link-color': '#63d4fa',
    '--tg-theme-secondary-bg-color': '#283d48',
    '--tg-theme-section-bg-color': '#1a2c33',
    '--tg-theme-section-header-text-color': '#63d4fa',
    '--tg-theme-subtitle-text-color': '#83a8b7',
    '--tg-theme-text-color': '#f5f5f5',
  },
  [ColorPalette.greenLight]: {
    theme: ThemeMode.LIGHT,
    '--tg-theme-accent-text-color': '#38a220',
    '--tg-theme-bg-color': '#ffffff',
    '--tg-theme-button-color': '#54b440',
    '--tg-theme-button-text-color': '#ffffff',
    '--tg-theme-destructive-text-color': '#d14e4e',
    '--tg-theme-header-bg-color': '#ffffff',
    '--tg-theme-hint-color': '#999999',
    '--tg-theme-link-color': '#38a220',
    '--tg-theme-secondary-bg-color': '#f1f1f1',
    '--tg-theme-section-bg-color': '#ffffff',
    '--tg-theme-section-header-text-color': '#38a220',
    '--tg-theme-subtitle-text-color': '#999999',
    '--tg-theme-text-color': '#000000',
  },
  [ColorPalette.greenDark]: {
    theme: ThemeMode.DARK,
    '--tg-theme-accent-text-color': '#94e088',
    '--tg-theme-bg-color': '#111810',
    '--tg-theme-button-color': '#476f42',
    '--tg-theme-button-text-color': '#ffffff',
    '--tg-theme-destructive-text-color': '#ec3942',
    '--tg-theme-header-bg-color': '#111810',
    '--tg-theme-hint-color': '#495747',
    '--tg-theme-link-color': '#96e38a',
    '--tg-theme-secondary-bg-color': '#192218',
    '--tg-theme-section-bg-color': '#111810',
    '--tg-theme-section-header-text-color': '#96e38a',
    '--tg-theme-subtitle-text-color': '#495747',
    '--tg-theme-text-color': '#f5f5f5',
  },
  [ColorPalette.pinkLight]: {
    theme: ThemeMode.LIGHT,
    '--tg-theme-accent-text-color': '#bf4b7f',
    '--tg-theme-bg-color': '#ffffff',
    '--tg-theme-button-color': '#d46c99',
    '--tg-theme-button-text-color': '#ffffff',
    '--tg-theme-destructive-text-color': '#d14e4e',
    '--tg-theme-header-bg-color': '#ffffff',
    '--tg-theme-hint-color': '#999999',
    '--tg-theme-link-color': '#bf4b7f',
    '--tg-theme-secondary-bg-color': '#f1f1f1',
    '--tg-theme-section-bg-color': '#ffffff',
    '--tg-theme-section-header-text-color': '#bf4b7f',
    '--tg-theme-subtitle-text-color': '#999999',
    '--tg-theme-text-color': '#000000',
  },
  [ColorPalette.pinkDark]: {
    theme: ThemeMode.DARK,
    '--tg-theme-accent-text-color': '#ed8abf',
    '--tg-theme-bg-color': '#25181f',
    '--tg-theme-button-color': '#aa6085',
    '--tg-theme-button-text-color': '#ffffff',
    '--tg-theme-destructive-text-color': '#ec3942',
    '--tg-theme-header-bg-color': '#25181f',
    '--tg-theme-hint-color': '#866b79',
    '--tg-theme-link-color': '#ee89bf',
    '--tg-theme-secondary-bg-color': '#34242b',
    '--tg-theme-section-bg-color': '#25181f',
    '--tg-theme-section-header-text-color': '#ee89bf',
    '--tg-theme-subtitle-text-color': '#866b79',
    '--tg-theme-text-color': '#f5f5f5',
  },
};
